import { useMemo } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { logException } from '@atlaskit/editor-common/monitoring';

import { BLOCK_SERVICE_API_URL } from './services/snippetMediaClient';
import {
	SnippetBodyFormat,
	type SnippetProduct,
	SnippetScope,
	type SnippetData,
	type SnippetsProvider,
	type Snippet,
	type UserInfo,
} from './types';
import { batchFetchUsers } from './utils/batchFetchUsers';
import { withSnippetsCache } from './utils/withSnippetsCache';

const SNIPPETS_PATH = '/snippets';
const AGG_PATH = '/gateway/api/graphql';

export interface FetchSnippetDataParams {
	format: SnippetBodyFormat;
	product?: SnippetProduct;
	scope: SnippetScope;
}

export type FetchSnippetsParams = Omit<FetchSnippetDataParams, 'scope'>;

export type SnippetProviderParams = FetchSnippetsParams;

type SnippetSettingsResponse = {
	sharingEnabled: boolean;
};

export interface FetchSnippetsResponse {
	error: Error[];
	success: SnippetData[];
}

export const getIsSnippetSharingForSiteEnabled = async (): Promise<boolean> => {
	try {
		const response = await fetch(`${BLOCK_SERVICE_API_URL}${SNIPPETS_PATH}/settings`, {
			method: 'GET',
		});
		const data: SnippetSettingsResponse = await response.json();
		return data.sharingEnabled;
	} catch (error) {
		logException(error as Error, {
			location: 'editor-snippets-provider/getIsSnippetSharingForSiteEnabled',
		});
		return false;
	}
};

export const fetchSnippets = async (params: FetchSnippetsParams): Promise<SnippetData[]> => {
	const isSnippetSharingForSiteEnabled = await getIsSnippetSharingForSiteEnabled();
	const [globalSnippetsResponse, siteSnippetsResponse, userSnippetsResponse] =
		await Promise.allSettled([
			fetchSnippetsData({ ...params, scope: SnippetScope.GLOBAL }),
			isSnippetSharingForSiteEnabled
				? fetchSnippetsData({ ...params, scope: SnippetScope.SITE })
				: Promise.resolve({ success: [] }),
			fetchSnippetsData({ format: params.format, scope: SnippetScope.USER }), // exclude product for user snippets
		]);

	const globalSnippets =
		globalSnippetsResponse.status === 'fulfilled' ? globalSnippetsResponse.value : { success: [] };
	const siteSnippets =
		siteSnippetsResponse.status === 'fulfilled' ? siteSnippetsResponse.value : { success: [] };
	const userSnippets =
		userSnippetsResponse.status === 'fulfilled' ? userSnippetsResponse.value : { success: [] };

	return [...globalSnippets.success, ...siteSnippets.success, ...userSnippets.success];
};

export const fetchSnippetsData = async (
	params: FetchSnippetDataParams,
): Promise<FetchSnippetsResponse> => {
	const queryParams = params.product
		? new URLSearchParams({
				format: params.format,
				product: params.product,
				scope: params.scope,
			})
		: new URLSearchParams({
				format: params.format,
				scope: params.scope,
			});

	const response = await fetch(
		`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}?${queryParams.toString()}`,
		{
			method: 'GET',
		},
	);

	if (!response.ok) {
		const error = new Error(`Failed to fetch snippets for scope: ${params.scope}`);
		logException(error, {
			location: 'editor-snippets-provider/fetchSnippetsData',
		});
		throw error;
	}

	return await response.json();
};

export const fetchUsersFromAgg = async (accountIds: string[]): Promise<UserInfo[]> => {
	const USERS_OPERATION_NAME = 'SnippetsProviderUsersQuery';
	const USERS_QUERY = `
	query ${USERS_OPERATION_NAME}($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			accountId
			name
		}
	}
`;
	try {
		const response = await fetch(AGG_PATH, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				operationName: USERS_OPERATION_NAME,
				query: USERS_QUERY,
				variables: { accountIds },
			}),
		});

		const json = await response.json();
		return (json?.data?.users ?? []).map(
			(u: { accountId?: string; name?: string; picture?: string }) => ({
				accountId: u.accountId ?? '',
				name: u.name ?? '',
			}),
		);
	} catch (error) {
		logException(error as Error, {
			location: 'editor-snippets-provider/fetchUsersFromAgg',
		});
		throw error;
	}
};

export const createSnippetsProvider = (params: SnippetProviderParams): SnippetsProvider => ({
	getSnippets: (): Promise<Snippet[]> =>
		withSnippetsCache(async () => {
			let snippets = await fetchSnippets(params);

			if (!snippets) {
				return [];
			}

			if (!Array.isArray(snippets)) {
				snippets = [snippets];
			}

			const parsedSnippets = snippets
				.map((snippet) => {
					const snippetBody = parseSnippetContent(snippet.body);
					if (snippetBody === undefined) {
						return undefined;
					}

					return { ...snippet, body: snippetBody } as Snippet;
				})
				.filter((snippet: Snippet | undefined): snippet is Snippet => snippet !== undefined);

			if (parsedSnippets.length === 0) {
				return parsedSnippets;
			}

			const accountIds = parsedSnippets.map((s) => s.createdBy);

			let userNameMap: Record<string, string> = {};
			try {
				const users = await batchFetchUsers(accountIds, fetchUsersFromAgg);
				userNameMap = Object.fromEntries(users.map((u) => [u.accountId, u.name]));
			} catch {
				// fetchUsers failure is non-fatal — fall back to raw account IDs
			}

			return parsedSnippets.map((snippet) => ({
				...snippet,
				createdBy: userNameMap[snippet.createdBy] ?? snippet.createdBy,
			}));
		}),
});

function filterValidAdfNodes(nodes: (ADFEntity | null | undefined)[]): ADFEntity[] {
	return nodes.filter((node): node is ADFEntity => node !== undefined && node !== null);
}

const parseSnippetContent = (content: string): ADFEntity | undefined => {
	try {
		const parsedContent = JSON.parse(content);

		// Handle fragment (array of nodes without doc wrapper)
		if (Array.isArray(parsedContent)) {
			return wrapAdfWithDocNode(filterValidAdfNodes(parsedContent));
		}

		const adfContent = parsedContent as ADFEntity;

		if (adfContent.type === 'doc' && Array.isArray(adfContent.content)) {
			return { ...adfContent, content: filterValidAdfNodes(adfContent.content) };
		}

		return wrapAdfWithDocNode(adfContent);
	} catch (error) {
		logException(error as Error, {
			location: 'editor-snippets-provider/parseSnippetContent',
		});
		return undefined;
	}
};

const wrapAdfWithDocNode = (adf: ADFEntity | ADFEntity[]): ADFEntity => {
	const docType = 'doc';
	const isArray = Array.isArray(adf);
	if (!isArray && adf.type === docType) {
		return adf;
	}

	const content: ADFEntity[] = isArray ? adf : [adf];
	return {
		version: 1,
		type: docType,
		content,
	};
};

export interface SnippetsPluginOptionsParams {
	product: SnippetProduct;
}

export const useSnippetsPluginOptions = ({
	product,
}: SnippetsPluginOptionsParams): { snippetsProvider: SnippetsProvider } =>
	useMemo(
		() => ({
			snippetsProvider: createSnippetsProvider({
				format: SnippetBodyFormat.ADF,
				product,
			}),
		}),
		[product],
	);
